import { useEffect, useRef } from "react";
import { Badge } from "~/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "~/components/ui/dialog";

type TimelineAsset = {
  id: string;
  type: string;
  toolName: string;
  label: string;
  dataUrl?: string;
  videoUrl?: string;
  videoBase64?: string;
  mediaType?: string;
  messageIndex: number;
  data: Record<string, unknown>;
};

const ASSET_ICONS: Record<string, string> = {
  generate_mood_board: "palette",
  generate_design_render: "image",
  adjust_design: "sliders",
  generate_catalog_render: "camera",
  render_from_sketch: "pencil-ruler",
  analyze_sketch: "scan",
  structural_analysis: "shield",
  calculate_bom: "receipt",
  material_recommendation: "gem",
  generate_turntable_video: "video",
  finalize_design: "check-circle",
  share_to_gallery: "image",
};

function TimelineIcon({ type }: { type: string }) {
  const icon = ASSET_ICONS[type] ?? "circle";
  const paths: Record<string, React.ReactNode> = {
    palette: <><circle cx="12" cy="12" r="10" /><circle cx="12" cy="8" r="1.5" fill="currentColor" /><circle cx="8.5" cy="12" r="1.5" fill="currentColor" /><circle cx="15.5" cy="12" r="1.5" fill="currentColor" /></>,
    image: <><rect width="18" height="18" x="3" y="3" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" /></>,
    sliders: <><line x1="4" x2="4" y1="21" y2="14" /><line x1="4" x2="4" y1="10" y2="3" /><line x1="12" x2="12" y1="21" y2="12" /><line x1="12" x2="12" y1="8" y2="3" /><line x1="20" x2="20" y1="21" y2="16" /><line x1="20" x2="20" y1="12" y2="3" /><line x1="2" x2="6" y1="14" y2="14" /><line x1="10" x2="14" y1="8" y2="8" /><line x1="18" x2="22" y1="16" y2="16" /></>,
    camera: <><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3Z" /><circle cx="12" cy="13" r="3" /></>,
    "pencil-ruler": <><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" /><path d="m15 5 4 4" /></>,
    scan: <><path d="M3 7V5a2 2 0 0 1 2-2h2" /><path d="M17 3h2a2 2 0 0 1 2 2v2" /><path d="M21 17v2a2 2 0 0 1-2 2h-2" /><path d="M7 21H5a2 2 0 0 1-2-2v-2" /><line x1="7" x2="17" y1="12" y2="12" /></>,
    shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" /></>,
    receipt: <><path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" /><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" /><path d="M12 17.5v-11" /></>,
    gem: <><path d="M6 3h12l4 6-10 13L2 9Z" /><path d="M11 3 8 9l4 13 4-13-3-6" /><path d="M2 9h20" /></>,
    video: <><path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.934a.5.5 0 0 0-.777-.416L16 11" /><rect width="14" height="12" x="2" y="6" rx="2" /></>,
    "check-circle": <><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><path d="m9 11 3 3L22 4" /></>,
    circle: <circle cx="12" cy="12" r="4" />,
  };

  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
    >
      {paths[icon]}
    </svg>
  );
}

function AssetCard({ asset, isSelected, onClick }: { asset: TimelineAsset; isSelected: boolean; onClick: () => void }) {
  const hasImage = !!asset.dataUrl;
  const hasVideo = !!(asset.videoUrl || asset.videoBase64);

  if (asset.type === "analyze_sketch") {
    const data = asset.data as {
      inferredJewelryType?: string;
      inferredStyle?: string;
      designNotes?: string[];
      clarificationNeeded?: string[];
    };
    return (
      <button
        onClick={onClick}
        className={`w-full text-left rounded-lg border p-3 transition-all duration-200 ${
          isSelected
            ? "border-blue-500/50 bg-blue-500/[0.08] ring-1 ring-blue-500/20"
            : "border-border/60 bg-card hover:border-border hover:bg-card/80"
        }`}
      >
        <div className="flex flex-wrap gap-1.5 mb-2">
          {data.inferredJewelryType && <Badge variant="outline" className="text-[9px] h-4">{data.inferredJewelryType}</Badge>}
          {data.inferredStyle && <Badge variant="outline" className="text-[9px] h-4">{data.inferredStyle}</Badge>}
        </div>
        {data.designNotes && data.designNotes.length > 0 && (
          <p className="text-[11px] text-muted-foreground line-clamp-2">{data.designNotes[0]}</p>
        )}
        {data.clarificationNeeded && data.clarificationNeeded.length > 0 && (
          <p className="text-[10px] text-blue-500 mt-1">{data.clarificationNeeded.length} question{data.clarificationNeeded.length !== 1 ? "s" : ""}</p>
        )}
      </button>
    );
  }

  if (asset.type === "structural_analysis") {
    const findings = (asset.data as { findings?: Array<{ severity: string; area: string; finding: string }> }).findings;
    const hasCritical = findings?.some((f) => f.severity === "critical");
    const hasWarning = findings?.some((f) => f.severity === "warning");
    return (
      <button
        onClick={onClick}
        className={`w-full text-left rounded-lg border p-3 transition-all duration-200 ${
          isSelected
            ? "border-foreground/20 bg-muted ring-1 ring-foreground/10"
            : "border-border/60 bg-card hover:border-border hover:bg-card/80"
        }`}
      >
        <Badge
          variant={hasCritical ? "destructive" : hasWarning ? "outline" : "default"}
          className="text-[9px] h-4 mb-1.5"
        >
          {hasCritical ? "ISSUES" : hasWarning ? "WARNINGS" : "PASS"}
        </Badge>
        <p className="text-[11px] text-muted-foreground">{findings?.length ?? 0} finding{(findings?.length ?? 0) !== 1 ? "s" : ""}</p>
      </button>
    );
  }

  if (asset.type === "calculate_bom" || asset.type === "finalize_design") {
    const data = asset.data as {
      metal?: { type: string; weightGrams: number };
      totalEstimate?: number;
      finalized?: boolean;
      title?: string;
    };
    return (
      <button
        onClick={onClick}
        className={`w-full text-left rounded-lg border p-3 transition-all duration-200 ${
          isSelected
            ? "border-emerald-500/40 bg-emerald-500/5 ring-1 ring-emerald-500/20"
            : "border-border/60 bg-card hover:border-border hover:bg-card/80"
        }`}
      >
        {data.finalized && <Badge className="text-[9px] h-4 mb-1.5 bg-emerald-600">FINALIZED</Badge>}
        {data.title && <p className="text-xs font-medium text-foreground mb-0.5 truncate">{data.title}</p>}
        {data.metal && <p className="text-[11px] text-muted-foreground">{data.metal.type} · {data.metal.weightGrams}g</p>}
        {data.totalEstimate != null && <p className="text-xs font-medium text-foreground mt-0.5">${data.totalEstimate.toLocaleString()}</p>}
      </button>
    );
  }

  if (asset.type === "material_recommendation") {
    const data = asset.data as { stone?: string; setting?: string; metal?: string; compatible?: boolean };
    return (
      <button
        onClick={onClick}
        className={`w-full text-left rounded-lg border p-3 transition-all duration-200 ${
          isSelected
            ? "border-foreground/20 bg-muted ring-1 ring-foreground/10"
            : "border-border/60 bg-card hover:border-border hover:bg-card/80"
        }`}
      >
        <Badge variant={data.compatible === false ? "destructive" : "default"} className="text-[9px] h-4 mb-1.5">
          {data.compatible === false ? "INCOMPATIBLE" : "COMPATIBLE"}
        </Badge>
        {data.stone && <p className="text-[11px] text-muted-foreground truncate">{data.stone} · {data.setting} · {data.metal}</p>}
      </button>
    );
  }

  if (hasVideo) {
    const src = asset.videoUrl ?? `data:${asset.mediaType || "video/mp4"};base64,${asset.videoBase64}`;
    return (
      <button
        onClick={onClick}
        className={`w-full text-left rounded-lg border overflow-hidden transition-all duration-200 ${
          isSelected
            ? "border-foreground/20 ring-1 ring-foreground/10"
            : "border-border/60 hover:border-border"
        }`}
      >
        <video src={src} muted playsInline loop autoPlay className="w-full aspect-square object-cover" />
      </button>
    );
  }

  if (hasImage) {
    return (
      <Dialog>
        <DialogTrigger asChild>
          <button
            onClick={onClick}
            className={`w-full text-left rounded-lg border overflow-hidden transition-all duration-200 group/img ${
              isSelected
                ? "border-foreground/20 ring-1 ring-foreground/10"
                : "border-border/60 hover:border-border"
            }`}
          >
            <div className="relative">
              <img src={asset.dataUrl} alt={asset.label} className="w-full aspect-square object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/10 transition-colors flex items-center justify-center">
                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" className="h-5 w-5 opacity-0 group-hover/img:opacity-80 transition-opacity drop-shadow-md">
                  <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                </svg>
              </div>
            </div>
          </button>
        </DialogTrigger>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          <img src={asset.dataUrl} alt={`${asset.label} — full size`} className="w-full" />
        </DialogContent>
      </Dialog>
    );
  }

  return null;
}

export function CaraAssetTimeline({
  assets,
  selectedAssetId,
  onSelectAsset,
}: {
  assets: TimelineAsset[];
  selectedAssetId: string | null;
  onSelectAsset: (id: string) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(assets.length);

  useEffect(() => {
    if (assets.length > prevCountRef.current && scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }
    prevCountRef.current = assets.length;
  }, [assets.length]);

  if (assets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-6 text-center">
        <div className="w-16 h-16 rounded-2xl border border-dashed border-border/60 flex items-center justify-center mb-4">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-7 w-7 text-muted-foreground/30">
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v13.5A1.5 1.5 0 003.75 21z" />
          </svg>
        </div>
        <p className="text-sm font-display text-foreground/60 mb-1">Asset Timeline</p>
        <p className="text-xs text-muted-foreground/50 max-w-[200px] leading-relaxed">
          Renders, analyses, and artifacts will appear here as Cara produces them.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="shrink-0 px-4 py-3 border-b border-border/60">
        <div className="flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-medium">Timeline</p>
          <span className="text-[10px] text-muted-foreground/50">{assets.length} asset{assets.length !== 1 ? "s" : ""}</span>
        </div>
      </div>

      {/* Timeline */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-[11px] top-2 bottom-2 w-px bg-border/40" />

          <div className="space-y-4">
            {assets.map((asset) => (
              <div key={asset.id} className="relative pl-8">
                {/* Timeline node */}
                <div
                  className={`absolute left-0 top-1 w-[23px] h-[23px] rounded-full border-2 flex items-center justify-center transition-colors ${
                    selectedAssetId === asset.id
                      ? "border-teal bg-teal/10 text-teal"
                      : "border-border bg-background text-muted-foreground/60"
                  }`}
                >
                  <TimelineIcon type={asset.toolName} />
                </div>

                {/* Label */}
                <p className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground/60 mb-1.5 font-medium">
                  {asset.label}
                </p>

                {/* Asset card */}
                <AssetCard
                  asset={asset}
                  isSelected={selectedAssetId === asset.id}
                  onClick={() => onSelectAsset(asset.id)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export type { TimelineAsset };
